import { useMemo, useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { useAuth } from '@/lib/auth/authContext';
import {
  dayPriceError,
  importedLabel,
  parseDayPrice,
  useApplyRateRange,
  useListingAvailability,
  useSetDayRate,
  type AvailabilityDay,
} from '@/lib/data/rates';
import { buildMonthGrid, monthLabel, nextMonth, previousMonth } from '@/lib/calendar/grid';
import { formatNad } from '@/lib/money/format';
import { ChevronBackIcon, ChevronForwardIcon } from '@/icons';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/rates/$listingId')({
  component: ListingRatesPage,
});

const WEEK_OPTS = { weekStartsOn: 1 as const };

function ListingRatesPage() {
  const { listingId } = useParams({ from: '/rates/$listingId' });
  const { supabase } = useAuth();
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const [showBulk, setShowBulk] = useState(false);

  const rangeStart = startOfWeek(startOfMonth(anchor), WEEK_OPTS);
  const rangeEnd = endOfWeek(endOfMonth(anchor), WEEK_OPTS);

  const availabilityQuery = useListingAvailability(supabase, listingId, rangeStart, rangeEnd);
  const setDayRate = useSetDayRate(supabase);
  const applyRange = useApplyRateRange(supabase);

  const byIso = useMemo(() => {
    const map = new Map<string, AvailabilityDay>();
    for (const day of availabilityQuery.data ?? []) map.set(day.date, day);
    return map;
  }, [availabilityQuery.data]);

  const weeks = useMemo(
    () =>
      buildMonthGrid(
        anchor,
        new Map(
          [...byIso.entries()].map(([iso, day]) => [iso, [day]]),
        ),
      ),
    [anchor, byIso],
  );

  const selectedDay = selectedIso ? byIso.get(selectedIso) ?? null : null;

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Rates for this listing</h1>
          <Link to="/rates" className="text-caption text-canyon underline">
            All listings
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setShowBulk((v) => !v)}
          className="rounded-button border border-border bg-surface px-md py-xs text-body font-semibold text-ink"
        >
          {showBulk ? 'Cancel bulk edit' : 'Apply price to a date range'}
        </button>
      </div>

      {showBulk ? (
        <BulkRateForm
          listingId={listingId}
          onApply={(input) =>
            applyRange.mutateAsync(input).then((result) => {
              // Keep the form open when days were skipped so the notice is seen.
              if (result.skipped === 0) setShowBulk(false);
              return result;
            })
          }
          pending={applyRange.isPending}
          error={applyRange.error instanceof Error ? applyRange.error.message : null}
        />
      ) : null}

      <div className="flex items-center justify-between gap-md rounded-card border border-border bg-surface px-md py-sm shadow-card">
        <button
          type="button"
          onClick={() => setAnchor((d) => previousMonth(d))}
          aria-label="Previous month"
          className="rounded-button p-xs text-ink hover:bg-surface-2"
        >
          <ChevronBackIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="text-body font-semibold text-ink">{monthLabel(anchor)}</span>
        <button
          type="button"
          onClick={() => setAnchor((d) => nextMonth(d))}
          aria-label="Next month"
          className="rounded-button p-xs text-ink hover:bg-surface-2"
        >
          <ChevronForwardIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {availabilityQuery.isLoading ? <PageLoading /> : null}

      <p className="text-caption text-ink-2">
        Prices tagged <span className="font-semibold text-sky">Season</span> come from a season on the Rates page. A price
        you set here replaces it for that day.
      </p>

      <div className="grid grid-cols-7 gap-xs">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-center text-caption font-semibold text-ink-2">
            {d}
          </div>
        ))}
        {weeks.flatMap((week) =>
          week.days.map((day) => {
            const info = day.items[0] as AvailabilityDay | undefined;
            const seasonPriced = !!info?.season_id && !info.is_closed;
            const imported = importedLabel(info);
            return (
              <button
                key={day.iso}
                type="button"
                title={seasonPriced ? 'Season price' : undefined}
                onClick={() => setSelectedIso(day.iso)}
                className={`min-h-[72px] rounded-card border p-xs text-left ${
                  day.inCurrentPeriod ? 'bg-surface' : 'bg-surface-2'
                } ${day.isToday ? 'ring-2 ring-canyon' : ''} ${
                  selectedIso === day.iso ? 'border-canyon' : 'border-border'
                }`}
              >
                <p className={`text-caption ${day.inCurrentPeriod ? 'text-ink' : 'text-ink-3'}`}>{format(day.date, 'd')}</p>
                {imported ? (
                  <>
                    <p className="mt-xs text-caption font-semibold text-warning">Blocked · {imported}</p>
                    {info?.price ? <p className="text-caption text-ink-2">{formatNad(info.price)}</p> : null}
                  </>
                ) : info?.is_closed ? (
                  <p className="mt-xs text-caption font-semibold text-danger">Closed</p>
                ) : info?.price ? (
                  <p className="mt-xs text-caption text-ink-2">{formatNad(info.price)}</p>
                ) : null}
                {seasonPriced ? <p className="text-caption font-semibold text-sky">Season</p> : null}
              </button>
            );
          }),
        )}
      </div>

      {selectedIso ? (
        <DayRateForm
          key={selectedIso}
          listingId={listingId}
          date={selectedIso}
          current={selectedDay}
          onSave={(input) => setDayRate.mutateAsync(input)}
          pending={setDayRate.isPending}
        />
      ) : null}
    </section>
  );
}

function DayRateForm({
  listingId,
  date,
  current,
  onSave,
  pending,
}: {
  listingId: string;
  date: string;
  current: AvailabilityDay | null;
  onSave: (input: { listingId: string; date: string; price: number | null; isClosed: boolean }) => Promise<unknown>;
  pending: boolean;
}) {
  const importedFrom = importedLabel(current);
  const [price, setPrice] = useState(current?.price != null ? String(current.price) : '');
  const [isClosedState, setIsClosed] = useState(!!current?.is_closed);
  // An imported day's open/closed state belongs to its calendar — always closed here.
  const isClosed = importedFrom ? true : isClosedState;
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  return (
    <form
      // The browser's min tooltip would pre-empt our copy, so native validation is off and dayPriceError
      // refuses a bad price here; the DB CHECK listing_availability_price_nonnegative backs it up.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setSaved(false);
        const priceError = dayPriceError(price);
        if (priceError) {
          setError(priceError);
          return;
        }
        onSave({ listingId, date, price: parseDayPrice(price), isClosed })
          .then(() => setSaved(true))
          .catch((err) => setError(err instanceof Error ? err.message : 'Could not save.'));
      }}
      className="flex flex-wrap items-end gap-md rounded-card border border-border bg-surface p-md shadow-card"
    >
      <p className="w-full text-body font-semibold text-ink">{date}</p>
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        Price (N$)
        <input
          type="number"
          min={0}
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          disabled={isClosed && !importedFrom}
          className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink disabled:opacity-50"
        />
      </label>
      <label className="flex items-center gap-xs text-body text-ink">
        <input
          type="checkbox"
          checked={isClosed}
          disabled={!!importedFrom}
          onChange={(e) => setIsClosed(e.target.checked)}
        />
        Closed
      </label>
      <button
        type="submit"
        disabled={pending}
        className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
      >
        {pending ? 'Saving…' : 'Save day'}
      </button>
      {error ? <p className="w-full text-body text-danger">{error}</p> : null}
      {importedFrom ? (
        <p className="w-full text-caption text-ink-2">
          Blocked by {importedFrom}. Change it in that calendar — it syncs every 15 minutes. You can still set a price.
        </p>
      ) : null}
      {saved ? <p className="w-full text-body text-success">Saved.</p> : null}
      {current?.season_id ? (
        <p className="w-full text-caption text-ink-2">
          This day has a season price. Saving a different price makes it a manual price that seasons won&apos;t change.
          Closing or reopening it keeps the season price.
        </p>
      ) : null}
    </form>
  );
}

function BulkRateForm({
  listingId,
  onApply,
  pending,
  error,
}: {
  listingId: string;
  onApply: (input: {
    listingId: string;
    startDate: string;
    endDate: string;
    price: number | null;
    isClosed: boolean;
  }) => Promise<{ applied: number; skipped: number }>;
  pending: boolean;
  error: string | null;
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [price, setPrice] = useState('');
  const [isClosed, setIsClosed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [result, setResult] = useState<{ applied: number; skipped: number } | null>(null);

  return (
    <form
      // The browser's min tooltip would pre-empt our copy, so native validation is off and dayPriceError
      // refuses a bad price here; the DB CHECK listing_availability_price_nonnegative backs it up.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setLocalError(null);
        setResult(null);
        if (!startDate || !endDate) {
          setLocalError('Pick a start and end date.');
          return;
        }
        const priceError = dayPriceError(price);
        if (priceError) {
          setLocalError(priceError);
          return;
        }
        onApply({ listingId, startDate, endDate, price: parseDayPrice(price), isClosed })
          .then(setResult)
          .catch(() => {
            /* surfaced via `error` prop */
          });
      }}
      className="flex flex-wrap items-end gap-md rounded-card border border-border bg-surface p-md shadow-card"
    >
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        From
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink" />
      </label>
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        To
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink" />
      </label>
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        Price (N$)
        <input
          type="number"
          min={0}
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          disabled={isClosed}
          className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink disabled:opacity-50"
        />
      </label>
      <label className="flex items-center gap-xs text-body text-ink">
        <input type="checkbox" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} />
        Closed
      </label>
      <button
        type="submit"
        disabled={pending}
        className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
      >
        {pending ? 'Applying…' : 'Apply'}
      </button>
      <p className="w-full text-caption text-ink-2">
        Prices applied here are manual and override any season on those days.
      </p>
      {result && result.skipped > 0 ? (
        <p className="text-caption text-ink-2">
          Updated {result.applied} {result.applied === 1 ? 'day' : 'days'}. Skipped {result.skipped}{' '}
          {result.skipped === 1 ? 'day' : 'days'} blocked by an imported calendar.
        </p>
      ) : null}
      {(localError || error) ? <p className="w-full text-body text-danger">{localError ?? error}</p> : null}
    </form>
  );
}
